import React, { ReactNode } from 'react';
import {
  Box, Flex, Text, useColorModeValue,
} from '@chakra-ui/react';
import DashPanel from './DashPanel';

// Recent activity across documents (item 66) — assembled client-side from the
// loaded lists' own audit timestamps (no event table exists). Each row deep-links
// via onOpen.
export interface ActivityItem {
  id: string;
  icon: ReactNode;
  label: string;
  sub: string;
  when: string; // ISO / date string
  accent: string; // chakra token
  href?: string;
}

// compact relative time from an ISO/date string (browser clock)
const timeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  if (s < 2629800) return `${Math.floor(s / 604800)}w ago`;
  return `${Math.floor(s / 2629800)}mo ago`;
};

const ActivityFeedCard: React.FC<{ items: ActivityItem[], onOpen?: (href: string) => void }> = ({ items, onOpen }) => {
  const labelColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const chipBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');

  return (
    <DashPanel label="Recent activity" accent="brandBlue.300">
      {items.length === 0 && <Text fontSize="sm" color={subColor}>Nothing recent.</Text>}
      <Flex direction="column">
        {items.map((it) => (
          <Flex
            key={it.id}
            align="center"
            gap={3}
            py={2.5}
            px={2}
            mx={-2}
            borderRadius="lg"
            cursor={it.href ? 'pointer' : 'default'}
            transition="background 0.15s"
            _hover={it.href ? { bg: rowHover } : undefined}
            onClick={() => (it.href && onOpen ? onOpen(it.href) : undefined)}
          >
            <Flex
              w="32px"
              h="32px"
              flexShrink={0}
              align="center"
              justify="center"
              borderRadius="lg"
              bg={chipBg}
              color={it.accent}
              fontSize="sm"
            >
              {it.icon}
            </Flex>
            <Box minW={0} flex={1}>
              <Text fontSize="sm" fontWeight="semibold" color={labelColor} noOfLines={1}>{it.label}</Text>
              <Text fontSize="xs" color={subColor} noOfLines={1}>{it.sub}</Text>
            </Box>
            <Text fontSize="xs" color={subColor} flexShrink={0}>{timeAgo(it.when)}</Text>
          </Flex>
        ))}
      </Flex>
    </DashPanel>
  );
};

ActivityFeedCard.defaultProps = { onOpen: undefined };

export default ActivityFeedCard;
